import React, { useState } from 'react';
import {
  Box,
  TextInput,
  Stack,
  Body,
  Textarea,
  useToast,
} from '@hover/blueprint';
import { useLocation } from '@reach/router';
import { useForm, Controller, UseFormReturn } from 'react-hook-form';
import axios, { AxiosResponse } from 'axios';
import { ArrowButton } from '@hover/shared-components';
import FileUploader from './FileUploader';
import { IJobQuestion } from '../../../interfaces/IJobQuestion';
import FormHeader from './FormHeader';
import TextInputWithLabel from './TextInputWithLabel';
import { IJobDemographicQuestions } from '../../../interfaces/IJobDemographicQuestion';
import MultiValueSingleSelect from './MultiValueSingleSelect';
import DemographicQuestions from './DemographicQuestions';

interface Props {
  id: number | string;
  title: string;
  office: string;
  department: string;
  questions: Array<IJobQuestion>;
  demographicQuestions: IJobDemographicQuestions;
}

interface ServerResponse {
  data: {
    response: AxiosResponse;
    message: string;
  };
}

const RIGHT_COLUMN_DATA = [
  'Resume/CV',
  'Cover Letter',
  'LinkedIn Profile',
  'Website',
];

const normalizeData = (questions: IJobQuestion[]) =>
  questions.reduce(
    (
      acc: { leftColumn: IJobQuestion[]; rightColumn: IJobQuestion[] },
      curr,
    ) => {
      if (RIGHT_COLUMN_DATA.includes(curr.label)) {
        return {
          rightColumn: [...acc.rightColumn, curr],
          leftColumn: acc.leftColumn,
        };
      }
      return {
        leftColumn: [...acc.leftColumn, curr],
        rightColumn: acc.rightColumn,
      };
    },
    {
      leftColumn: [],
      rightColumn: [],
    },
  );

const JobPostsForm: React.FC<Props> = ({
  title,
  office,
  department,
  questions,
  demographicQuestions,
  id,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { search } = useLocation();

  const GH_SRC = new URLSearchParams(search).get('gh_src');

  const errorToast = useToast({
    status: 'error',
    isClosable: true,
  });
  const successToast = useToast({
    status: 'success',
    isClosable: true,
  });

  const {
    register,
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  }: UseFormReturn = useForm();
  const { rightColumn, leftColumn } = normalizeData(questions);

  const onRemove = (fieldName: string) => {
    setValue(fieldName, null);
  };

  const onSubmit = async (data: { [x: string]: any }) => {
    setIsLoading(true);
    const inputData = { ...data };
    const toBase64 = (file: File) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });

    const formData = new FormData();
    const defaultValues = Object.keys(inputData).reduce(
      (acc, curr) => ({ ...acc, [curr]: null }),
      {},
    );
    if (inputData.resume && inputData.resume instanceof File) {
      const base64ResumeFile = (await toBase64(inputData.resume)) as string;
      const formattedBase64 = base64ResumeFile.split(',').pop() as string;
      formData.append('resume_content', formattedBase64);
      formData.append('resume_content_filename', inputData.resume.name);
      delete inputData.resume;
    }
    if (inputData.cover_letter && inputData.cover_letter instanceof File) {
      const base64CoverLetterFile = (await toBase64(
        inputData.cover_letter,
      )) as string;
      const formattedBase64Cover = base64CoverLetterFile
        .split(',')
        .pop() as string;
      formData.append('cover_letter_content', formattedBase64Cover);
      formData.append(
        'cover_letter_content_filename',
        inputData.cover_letter.name,
      );
      delete inputData.cover_letter;
    }
    if (GH_SRC) inputData.mapped_url_token = GH_SRC;
    Object.keys(inputData).forEach(key => {
      if (inputData[key]) {
        if (Array.isArray(inputData[key])) {
          formData.set(key, JSON.stringify(inputData[key]));
        } else {
          formData.set(key, inputData[key]);
        }
      }
    });
    try {
      const response: ServerResponse = await axios.post(
        `/pages-api/post-application/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setIsLoading(false);
      if (response.data.message === 'Submitted') {
        reset(defaultValues);
        successToast({ description: 'Application is submitted' });
      } else {
        errorToast({ description: 'Application is not submitted' });
      }
    } catch (e) {
      setIsLoading(false);
      errorToast({ description: 'Application is not submitted' });
    }
  };

  return (
    <Box flexDir="column" alignItems="flex-start" w="100%" position="relative">
      <Box
        position="absolute"
        top={{ base: '-80px', desktop: '-110px' }}
        left={0}
        id="job-post-form"
      />
      <FormHeader jobTitle={title} office={office} department={department} />
      <Stack
        w="100%"
        spacing={{ base: 700, tablet: 1000, desktop: '134px' }}
        direction={{ base: 'column-reverse', tablet: 'row' }}
        alignItems="flex-start"
        mb={1000}
      >
        <Box w="100%" maxW={{ base: '100%', tablet: '687px' }} flexDir="column">
          {leftColumn.map((item, index) => {
            const { fields } = item;
            const { message } = (errors[fields[0].name] || {}) as {
              message: string;
            };

            const emailValidation =
              fields[0].name === 'email'
                ? {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'invalid email address',
                    },
                  }
                : {};

            if (fields[0].type === 'input_text') {
              return (
                <TextInputWithLabel
                  required={item.required}
                  errorMessage={message}
                  registerRef={{
                    ...register(fields[0].name, {
                      required: {
                        value: item.required,
                        message: 'Required',
                      },
                      ...emailValidation,
                    }),
                  }}
                  index={index}
                  label={item.label}
                />
              );
            }
            if (fields[0].type === 'multi_value_single_select') {
              return (
                <MultiValueSingleSelect
                  required={item.required}
                  control={control}
                  jobQuestion={item}
                  errorMessage={message}
                  rules={{
                    required: {
                      value: item.required,
                      message: 'Required',
                    },
                  }}
                />
              );
            }
            if (fields[0].type === 'textarea') {
              return (
                <Box flexDir="column">
                  <Body mb={100}>
                    {item.label}
                    {item.required && '*'}
                  </Body>
                  <Textarea
                    rows={3}
                    borderColor={message ? 'orange.500' : 'neutral.300'}
                    _placeholder={{
                      color: message ? 'orange.600' : 'brandNavy.300',
                      fontWeight: 700,
                      fontSize: '12px',
                      letterSpacing: '0.1rem',
                    }}
                    placeholder="YOUR ANSWER"
                    {...register(fields[0].name)}
                    mb="30px"
                  />
                </Box>
              );
            }
            return <></>;
          })}
          {demographicQuestions && (
            <DemographicQuestions
              setValue={setValue}
              control={control}
              demographicQuestions={demographicQuestions}
            />
          )}
          <ArrowButton
            isLoading={isLoading}
            mt="30px"
            onClick={handleSubmit(onSubmit)}
            textTransform="uppercase"
            w={{ base: '100%', tablet: '250px' }}
          >
            submit application
          </ArrowButton>
        </Box>
        <Box
          flexDir="column"
          w="100%"
          maxW={{ base: '100%', tablet: '379px' }}
          bg="primary.100"
          borderRadius="8px"
          px={400}
          overflow="hidden"
          py={500}
        >
          {rightColumn.map((item, index) => {
            const { fields } = item;
            const { message } = (errors[fields[0].name] || {}) as {
              message: string;
            };

            if (fields[0].type === 'input_file') {
              return (
                <Controller
                  name={fields[0].name}
                  control={control}
                  rules={{
                    required: {
                      value: item.required,
                      message: 'Required',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <FileUploader
                      value={value as File}
                      errorMessage={message}
                      mb={
                        rightColumn.length < 3 &&
                        index + 1 === rightColumn.length
                          ? 0
                          : 600
                      }
                      title={`attach ${item.label}`}
                      label={item.label}
                      onChangeFile={onChange}
                      onRemove={() => {
                        onRemove(item.fields[0].name);
                      }}
                    />
                  )}
                />
              );
            }
            return (
              <TextInput
                borderColor="neutral.300"
                placeholder={item.label.toUpperCase()}
                _placeholder={{
                  color: 'brandNavy.300',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.1rem',
                }}
                {...register(fields[0].name)}
                mb={index + 1 === rightColumn.length ? 0 : 600}
              />
            );
          })}
        </Box>
      </Stack>
    </Box>
  );
};

export default JobPostsForm;
